import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';

/**
 * Map a Firestore QuerySnapshot to an array of `{ id, ...data }`.
 * @param {import('firebase/firestore').QuerySnapshot} snap
 * @return {Array<object>}
 */
export function mapQueryDocs(snap) {
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to a Firestore query with onSnapshot for live UI updates.
 * Pass a stable `query` from useMemo in the parent component.
 *
 * @param {import('firebase/firestore').Query | null | undefined} query
 * @param {{
 *   enabled?: boolean,
 *   mapDocs?: (snap: import('firebase/firestore').QuerySnapshot) => unknown[],
 *   silentPermissionDenied?: boolean,
 * }} [options]
 */
export function useFirestoreQuery(query, options = {}) {
  const enabled = options.enabled !== false;
  const mapDocs = options.mapDocs || mapQueryDocs;
  const silentPermissionDenied = options.silentPermissionDenied === true;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled && query));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !query) {
      setData([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      query,
      (snap) => {
        setData(mapDocs(snap));
        setLoading(false);
        setError(null);
      },
      (err) => {
        const permissionDenied = err?.code === 'permission-denied';
        if (!silentPermissionDenied || !permissionDenied) {
          console.error('useFirestoreQuery:', err);
        }
        setError(permissionDenied && silentPermissionDenied ? null : err);
        if (permissionDenied && silentPermissionDenied) {
          setData([]);
        }
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [query, enabled, mapDocs, silentPermissionDenied]);

  return { data, loading, error };
}
